import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { AriaComponent, LegendComponent, TitleComponent, TooltipComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import { ECBasicOption } from 'echarts/types/dist/shared';
import {
  ECHARTS_BASE_OPTIONS,
  ECHARTS_GENERATIVE_THEME,
  ECHARTS_GENERATIVE_THEME_NAME,
} from '@shared/constants/custom-report-theme.constants';
import { IChatV2ComponentChunk } from '@shared/interfaces/ai-executor.interface';
import { severityColors } from '../../../../constants/charts.constants';
import { EchartsService } from '../../../../services/echarts.service';
import { DEFAULT_LEGEND_WIDTH_PX, estimateLegendRows, LEGEND_ITEM_GAP_PX, LEGEND_ROW_HEIGHT_PX } from '../chart-legend.utility';
import { toMarkdownTable } from '../generative-components.utils';

export interface IPieChartDataItem {
  name: string;
  value: number;
}

// Matches ECHARTS_GENERATIVE_THEME's colour/decal palette length.
const MAX_PIE_CHART_CATEGORIES = 8;
const OTHER_CATEGORY_LABEL = 'Other';

function capCategories(items: IPieChartDataItem[]): IPieChartDataItem[] {
  if (items.length <= MAX_PIE_CHART_CATEGORIES) {
    return items;
  }

  const sorted = [...items].sort((a: IPieChartDataItem, b: IPieChartDataItem) => b.value - a.value);
  const kept = sorted.slice(0, MAX_PIE_CHART_CATEGORIES - 1);
  const otherValue = sorted
    .slice(MAX_PIE_CHART_CATEGORIES - 1)
    .reduce((sum: number, item: IPieChartDataItem) => sum + item.value, 0);

  return [...kept, { name: OTHER_CATEGORY_LABEL, value: Math.round(otherValue * 100) / 100 }];
}

const LEGEND_TOP_GAP_PX = 12;
const LEGEND_BOTTOM_PADDING_PX = 8;
const PIE_RADIUS_PX: [number, number] = [58, 90];
const PIE_CENTER_Y_PX = 100;
const PIE_BOTTOM_EDGE_PX = PIE_CENTER_Y_PX + PIE_RADIUS_PX[1];
const LEGEND_TOP_PX = PIE_BOTTOM_EDGE_PX + LEGEND_TOP_GAP_PX;

@Component({
  selector: 'app-ai-chat-pie-chart',
  templateUrl: './ai-chat-pie-chart.component.html',
  styleUrls: ['./ai-chat-pie-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EchartsService],
  imports: [CommonModule],
  standalone: true,
})
export class AiChatPieChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() public chunk: IChatV2ComponentChunk;

  @ViewChild('chartTarget') private chartTarget: ElementRef<HTMLDivElement>;

  public title: string = '';
  public accessibleItems: IPieChartDataItem[] = [];
  public chartHeight: number = 300;

  private chart: echarts.EChartsType;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private echartsService: EchartsService,
  ) {}

  public ngAfterViewInit(): void {
    this.initChart();
  }

  public ngOnChanges(): void {
    if (this.chart) {
      this.initChart();
    }
  }

  public ngOnDestroy(): void {
    this.echartsService.disposeAll();
  }

  private withSeverityColor(item: IPieChartDataItem): IPieChartDataItem & { itemStyle?: { color: string } } {
    const color = (severityColors as Record<string, string | undefined>)[item.name.toLowerCase()];
    return color !== undefined ? { ...item, itemStyle: { color } } : item;
  }

  public static serialize(data: Record<string, unknown>): string {
    const title = (data['title'] as string) ?? '';
    const items = capCategories((data['data'] as IPieChartDataItem[]) ?? []);

    const columns = ['Name', 'Value'];
    const rows = items.map((item: IPieChartDataItem) => [item.name, String(item.value)]);
    const table = toMarkdownTable(columns, rows);

    return title ? `${title}\n${table}` : table;
  }

  private initChart(): void {
    if (!this.chartTarget) {
      return;
    }

    const data = this.chunk?.data ?? {};
    const items = capCategories((data['data'] as IPieChartDataItem[]) ?? []);
    this.title = (data['title'] as string) ?? '';
    this.accessibleItems = items;

    const containerWidthPx = this.chartTarget.nativeElement.clientWidth || DEFAULT_LEGEND_WIDTH_PX;
    const legendRows = estimateLegendRows(
      items.map((item: IPieChartDataItem) => item.name),
      containerWidthPx,
    );
    this.chartHeight = LEGEND_TOP_PX + legendRows * LEGEND_ROW_HEIGHT_PX + LEGEND_BOTTOM_PADDING_PX;

    // Must run before echarts measures the container, or it uses the stale height.
    this.changeDetectorRef.detectChanges();

    echarts.use([PieChart, AriaComponent, LegendComponent, TitleComponent, TooltipComponent, SVGRenderer]);
    echarts.registerTheme(ECHARTS_GENERATIVE_THEME_NAME, ECHARTS_GENERATIVE_THEME);

    this.chart = this.echartsService.initialize(this.chartTarget.nativeElement, ECHARTS_GENERATIVE_THEME_NAME);

    const option: ECBasicOption = {
      ...ECHARTS_BASE_OPTIONS,
      legend: { type: 'plain', orient: 'horizontal', top: LEGEND_TOP_PX, itemGap: LEGEND_ITEM_GAP_PX },
      series: [
        {
          type: 'pie',
          radius: PIE_RADIUS_PX,
          center: ['50%', PIE_CENTER_Y_PX],
          label: { show: false },
          data: items.map((item: IPieChartDataItem) => this.withSeverityColor(item)),
        },
      ],
    };

    this.chart.setOption(option);
  }
}
